import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { apiReports } from "../api/reports";
import Sparkline from "./Sparkline";
import CountyChoroplethMap from "./CountyChoroplethMap";
import { monitoringToday, monitoringStart, monitoringRange, monitoringDates, monitoringDateLabel } from "./timeMonitoringUtils";
import { schedulingBookingView } from "./schedulingAnalyticsUtils";

const control = "tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm focus:tw-ring-2 focus:tw-ring-blue-500";
const bookingPercent = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });
const chartOptions = {
  maintainAspectRatio: false,
  animation: false,
  legend: { display: false },
  scales: {
    yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Policies booked" } }],
    xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12, maxRotation: 45, minRotation: 0 } }],
  },
  tooltips: { mode: "index", intersect: false },
};

function BookingChart({ daily }) {
  return <div style={{ height: 240 }} role="img" aria-label="Policies booked by Eastern calendar date">
    <Line data={{ labels: daily.map((day) => monitoringDateLabel(day.day)), datasets: [{
      label: "Policies booked", data: daily.map((day) => day.scheduled), borderColor: "#94ba33",
      backgroundColor: "rgba(148,186,51,0.12)", borderWidth: 2, fill: true, lineTension: 0.2, pointRadius: 2,
    }] }} options={chartOptions} />
  </div>;
}

function BookingBreakdown({ title, rows, total, weekly = false, onSelect }) {
  return <section aria-label={title} className="tw-min-w-0">
    <h4 className="tw-text-base tw-font-semibold tw-mt-0 tw-mb-3">{title}</h4>
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-text-sm">
        <thead className="tw-bg-gray-50 tw-text-gray-500"><tr>
          <th className="tw-text-left tw-font-medium tw-p-3">{weekly ? "Person" : "Name"}</th>
          <th className="tw-text-right tw-font-medium tw-p-3">Policies</th>
          <th className="tw-text-right tw-font-medium tw-p-3 tw-whitespace-nowrap">{weekly ? "Weekly trend" : "Share"}</th>
        </tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id} className="tw-border-t tw-border-gray-100">
          <td className="tw-p-3">{onSelect ? <button type="button" className="tw-text-left tw-text-blue-900 tw-font-medium tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500"
            onClick={() => onSelect(row.id)}>{row.name}</button> : row.name}</td>
          <td className="tw-p-3 tw-text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{row.scheduled.toLocaleString()}</td>
          <td className="tw-p-3 tw-text-right">{weekly ? <Sparkline data={{ values: row.weekly }} /> : bookingPercent.format(total ? row.scheduled / total : 0)}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>;
}

function BookingDetails({ selection, buckets, dates, onClose }) {
  const dialog = useRef(null);
  const view = useMemo(() => schedulingBookingView(buckets.filter((row) => selection.kind === "person"
    ? row.userId === selection.id : row.workAreaId === selection.id), dates), [selection, buckets, dates]);
  const person = selection.kind === "person";
  const title = person ? view.people[0]?.name : view.areas[0]?.name;
  useEffect(() => {
    const node = dialog.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    node.showModal();
    return () => { node.close(); document.body.style.overflow = overflow; };
  }, []);
  return <dialog ref={dialog} aria-labelledby="booking-detail-title" onCancel={onClose}
    className="tw-p-0 tw-border-0 tw-rounded-xl tw-shadow-2xl tw-text-gray-800"
    style={{ width: "min(1040px, calc(100vw - 24px))", maxHeight: "calc(100vh - 32px)" }}
    onClick={(event) => {
      if (event.target !== dialog.current) {return;}
      const r = dialog.current.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {onClose();}
    }}>
    <div className="tw-sticky tw-top-0 tw-z-20 tw-flex tw-justify-between tw-items-start tw-gap-4 tw-p-6 tw-bg-gray-50 tw-border-b tw-border-gray-200">
      <div><h3 id="booking-detail-title" className="tw-text-2xl tw-font-semibold tw-m-0">{title}</h3>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-0 tw-mt-1">{view.total.toLocaleString()} policies · {monitoringDateLabel(dates[0])} – {monitoringDateLabel(dates[dates.length - 1])} · Eastern time</p></div>
      <button type="button" className={control} onClick={onClose} aria-label="Close booking details">✕</button>
    </div>
    <div className="tw-p-6">
      {person ? <>
        <h4 className="tw-text-base tw-font-semibold tw-mt-0 tw-mb-3">Policies booked per day</h4><BookingChart daily={view.daily} />
        <div className="tw-mt-6"><BookingBreakdown title="Booked by area" rows={view.areas} total={view.total} /></div>
      </> : <>
        <CountyChoroplethMap counties={view.counties} metric="scheduled" />
        <div className="tw-grid md:tw-grid-cols-2 tw-gap-6 tw-mt-6">
          <section aria-label="Counties"><h4 className="tw-text-base tw-font-semibold tw-mt-0 tw-mb-3">Counties</h4>
            <div className="tw-overflow-y-auto" style={{ maxHeight: 320 }}><table className="tw-w-full tw-text-sm">
              <thead className="tw-bg-gray-50 tw-text-gray-500"><tr><th className="tw-p-3 tw-text-left tw-font-medium">County</th><th className="tw-p-3 tw-text-right tw-font-medium">Policies</th></tr></thead>
              <tbody>{view.counties.map((county) => <tr key={`${county.state}|${county.county}`} className="tw-border-t tw-border-gray-100">
                <td className="tw-p-3">{county.county || "Unknown county"}{county.state ? `, ${county.state}` : ""}</td><td className="tw-p-3 tw-text-right">{county.scheduled}</td>
              </tr>)}</tbody>
            </table></div>
          </section>
          <BookingBreakdown title="Booked by person" rows={view.people} total={view.total} />
        </div>
      </>}
    </div>
  </dialog>;
}

export default function SchedulingBookingAnalytics() {
  const today = monitoringToday();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [selection, setSelection] = useState(null);
  const { from, to } = monitoringRange("month", `${month}-01`, today);
  const dates = useMemo(() => monitoringDates(from, to), [from, to]);
  const ready = data?.from === from && data?.to === to;
  const view = useMemo(() => schedulingBookingView(ready ? data.buckets : [], dates), [data, dates, ready]);
  useEffect(() => {
    let active = true;
    setData(null); setError(""); setSelection(null);
    apiReports.getSchedulingAnalytics(from, to).then((response) => {
      if (!active) {return;}
      const result = response.data.result;
      if (response.data.status !== 200 || result?.from !== from || result?.to !== to || !Array.isArray(result?.buckets) || !Number.isInteger(result?.total)) {
        throw new Error("Invalid booking analytics response");
      }
      setData(result);
    }).catch(() => {if (active) {setError("Booking trends could not be loaded.");}});
    return () => { active = false; };
  }, [from, to, retry]);
  return <section aria-label="Booking trends and areas" className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 sm:tw-p-6 tw-my-8 tw-bg-white">
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-mb-5">
      <div><h3 className="tw-text-xl tw-font-semibold tw-m-0">Booking trends &amp; areas</h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-mb-0">By booking date · Eastern time{ready ? ` · ${data.total.toLocaleString()} policies` : ""}</p></div>
      <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm" htmlFor="booking-month">Month
        <input id="booking-month" type="month" className={control} min={monitoringStart.slice(0, 7)} max={today.slice(0, 7)} value={month}
          onChange={(event) => {const value = event.target.value; if (value >= monitoringStart.slice(0, 7) && value <= today.slice(0, 7)) {setMonth(value);}}} />
      </label>
    </div>
    {error ? <div role="alert" className="tw-text-sm tw-text-red-700">{error}
      <button type="button" className={`${control} tw-ml-3`} onClick={() => setRetry((value) => value + 1)}>Retry</button></div>
      : !ready ? <div role="status" className="tw-py-10 tw-text-center tw-text-gray-500">Loading booking trends…</div>
        : !view.total ? <p className="tw-text-sm tw-text-gray-500 tw-py-6">No policies booked in this month.</p>
          : <>
            <h4 className="tw-text-base tw-font-semibold tw-mt-0 tw-mb-3">Policies booked per day</h4><BookingChart daily={view.daily} />
            <div className="tw-grid lg:tw-grid-cols-2 tw-gap-8 tw-mt-6">
              <BookingBreakdown title="Bookings by person" rows={view.people} total={view.total} weekly onSelect={(id) => setSelection({ kind: "person", id })} />
              <BookingBreakdown title="Policies booked by area" rows={view.areas} total={view.total} onSelect={(id) => setSelection({ kind: "area", id })} />
            </div>
          </>}
    {ready && selection && <BookingDetails key={`${selection.kind}-${selection.id}`} selection={selection} buckets={data.buckets} dates={dates} onClose={() => setSelection(null)} />}
  </section>;
}
